import { Overlay } from '../overlay';

export class OverlayKeyboardDispatcher {
	/** Currently attached overlays in the order they were attached. */
	private attachedOverlays: Overlay[] = [];

	private document: Document;
	private isAttached: boolean;

	constructor(document: Document) {
		this.document = document;
	}

	/** Add a new overlay to the list of attached overlay refs. */
	add(overlayRef: Overlay): void {
		// Lazily start dispatcher once first overlay is added
		if (!this.isAttached) {
			this.document.body.addEventListener(
				'keydown',
				this.keydownListener,
				true
			);
			this.isAttached = true;
		}

		this.attachedOverlays.push(overlayRef);
	}

	/** Remove an overlay from the list of attached overlay refs. */
	remove(overlayRef: Overlay): void {
		const index = this.attachedOverlays.indexOf(overlayRef);

		if (index > -1) {
			this.attachedOverlays.splice(index, 1);
		}

		// Remove the global listener once there are no more overlays.
		if (this.attachedOverlays.length === 0) {
			this._detach();
		}
	}

	/** Select the appropriate overlay from a keydown event. */
	private _selectOverlayFromEvent(event: KeyboardEvent): Overlay {
		// Check if any overlays contain the event
		const targetedOverlay = this.attachedOverlays.find((overlay) => {
			return (
				overlay.overlayElement === event.target ||
				overlay.overlayElement.contains(event.target as HTMLElement)
			);
		});

		// Use the overlay if it exists, otherwise choose the most recently attached one
		return (
			targetedOverlay || this.attachedOverlays[this.attachedOverlays.length - 1]
		);
	}

	/** Detaches the global keyboard event listener. */
	private _detach() {
		if (this.isAttached) {
			this.document.body.removeEventListener(
				'keydown',
				this.keydownListener,
				true
			);
			this.isAttached = false;
		}
	}

	/** Keyboard event listener that will be attached to the body. */
	private keydownListener = (event: KeyboardEvent) => {
		if (this.attachedOverlays.length) {
			// Dispatch keydown event to the correct overlay.
			const overlay = this._selectOverlayFromEvent(event);

			if (overlay.onKeyboardEvent) {
				overlay.onKeyboardEvent(event);
			}
		}
	};
}
